import { useEffect, useRef } from "react";
import { supabase } from "./supabase.js";

const DEBOUNCE_MS = 600; // several rows change at once when a booking is made; refresh once
const POLL_CONNECTED_MS = 60_000; // safety net while live updates are working
const POLL_OFFLINE_MS = 20_000; // when they are not (connection dropped, table not enabled), poll like a plain auto-refresh

/**
 * Calls `onChange` whenever data this screen shows may have changed:
 *   - instantly, when one of `tables` changes for this business (Supabase Realtime; the database's own access rules decide
 *     what each signed-in user may receive);
 *   - when the app comes back to the foreground or the connection returns;
 *   - on a timer while the page is visible (every minute if live updates are connected, every 20 seconds if not),
 *     so the screen is never more than seconds stale even if Realtime is off.
 * `onChange` should re-read the data quietly (no loading screen). Bursts are merged into one call.
 */
export function useLiveRefresh(businessId: string | null, tables: readonly string[], onChange: () => void): void {
  const latest = useRef(onChange);
  latest.current = onChange;
  const tableKey = tables.join(",");

  useEffect(() => {
    if (!businessId) return;
    let connected = false;
    let debounce: ReturnType<typeof setTimeout> | undefined;
    let poll: ReturnType<typeof setTimeout> | undefined;

    const fire = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => latest.current(), DEBOUNCE_MS);
    };
    const visible = () => typeof document === "undefined" || document.visibilityState !== "hidden";
    const schedulePoll = () => {
      clearTimeout(poll);
      poll = setTimeout(() => {
        if (visible()) latest.current();
        schedulePoll();
      }, connected ? POLL_CONNECTED_MS : POLL_OFFLINE_MS);
    };

    let channel = supabase.channel(`live:${businessId}:${tableKey}`);
    for (const table of tableKey.split(",")) {
      channel = channel.on("postgres_changes", { event: "*", schema: "public", table, filter: `business_id=eq.${businessId}` }, fire);
    }
    channel.subscribe((status: string) => {
      const wasConnected = connected;
      connected = status === "SUBSCRIBED";
      if (connected && !wasConnected) fire(); // catch anything missed while (re)connecting
      schedulePoll();
    });

    const onWake = () => {
      if (visible()) fire();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("online", onWake);
    window.addEventListener("focus", onWake);
    schedulePoll();

    return () => {
      clearTimeout(debounce);
      clearTimeout(poll);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("online", onWake);
      window.removeEventListener("focus", onWake);
      void supabase.removeChannel(channel);
    };
  }, [businessId, tableKey]);
}
